/**
 * Форматтер для телефонных номеров
 */

import { formatIncompletePhoneNumber as libFormatIncompletePhoneNumber, type CountryCode } from 'libphonenumber-js';

import { parsePhoneNumber } from '../validators/phone-validators';

/**
 * Форматирует номер телефона по шаблону страны
 * @param phone - номер телефона
 * @param country - код страны (опционально, определяется автоматически)
 * @returns отформатированный номер или исходную строку при ошибке
 *
 * @example
 * formatPhoneNumber('+79991234567') // '+7 (999) 123-45-67'
 * formatPhoneNumber('89991234567', 'RU') // '+7 (999) 123-45-67'
 * formatPhoneNumber('+77001234567') // '+7 (700) 123-45-67'
 */
export function formatPhoneNumber(phone: string | number | null | undefined, country?: CountryCode): string {
  if (!phone) {
    return '';
  }

  // Добавляем + если номер не начинается с +
  let phoneStr = String(phone);
  if (!phoneStr.startsWith('+')) {
    phoneStr = '+' + phoneStr;
  }

  const phoneNumber = parsePhoneNumber(phoneStr, country);
  if (!phoneNumber) {
    return String(phone);
  }

  // Для остальных стран - международный формат
  return phoneNumber.formatInternational();
}

/**
 * Форматирует неполный номер телефона по мере ввода
 * Автоматически определяет страну и применяет соответствующий формат
 *
 * @param phone - частично введённый номер телефона
 * @param defaultCountry - страна по умолчанию (если не удаётся определить из номера)
 * @returns отформатированный номер
 *
 * @example
 * formatIncompletePhoneNumber('+7999') // '+7 999'
 * formatIncompletePhoneNumber('+79991234567') // '+7 (999) 123-45-67'
 * formatIncompletePhoneNumber('+1415') // '+1 415'
 * formatIncompletePhoneNumber('9991234567', 'RU') // '(999) 123-45-67'
 */
export function formatIncompletePhoneNumber(
  phone: string | number | null | undefined,
  defaultCountry: CountryCode = 'RU',
): string {
  if (!phone) {
    return '';
  }

  const phoneStr = String(phone);

  try {
    return libFormatIncompletePhoneNumber(phoneStr, defaultCountry);
  } catch {
    // Если форматирование не удалось, возвращаем исходную строку
    return phoneStr;
  }
}
